/**
 * Base URL for the Zendesk Web Widget loader. The `key` query param is the
 * per-site `zendeskChatSnippetId` site preference. Mirrors SFRA
 * `org_hec/cartridge/templates/default/components/footer/zendeskChat.isml:5`.
 */
const ZENDESK_SNIPPET_BASE = "https://static.zdassets.com/ekr/snippet.js?key=";

export interface ZendeskGate {
  /** `zendeskChatEnabled` site preference. */
  enabled: boolean;
  /** `zendeskChatSnippetId` site preference. */
  snippetId: string;
  /**
   * Whether the request is a TPA/ISE iframe context (`isTpaRequest`). SFRA
   * suppresses the widget for both `isTPA()` and `isISE()`; the Workers
   * `isTpaRequest` host-prefix check (`tpa.` / `ise.` / `cdn.` + the
   * `X-TPA-Has-Context` header) covers both, so a single flag is enough.
   */
  isTpa: boolean;
}

/**
 * Whether `pathname` is inside the telehealth intake flow (`/intake/*`, which
 * includes `/intake/:treatment/plan`). The chat widget is suppressed there
 * (ET-629) — the bubble must not show from intake start through plan
 * selection. Checkout after plan selection is SFRA, so the whole prefix.
 */
export function isIntakePath(pathname: string): boolean {
  return pathname === "/intake" || pathname.startsWith("/intake/");
}

/**
 * Resolve the Zendesk snippet `src` for the current request, or `null` when the
 * widget must not load. Pure function — the gating side of SFRA's
 * `zendeskHelper.showChat()` (`!(isTPA() || isISE() || !zendeskChatEnabled)`)
 * combined with the `zendeskChat.isml` `ZenDeskChatId` truthiness guard.
 *
 * Returns `null` (no script rendered) when the request is TPA/ISE, the
 * `zendeskChatEnabled` preference is false, or no snippet id is configured.
 *
 * `snippetId` is merchant-controlled site-preference config (not user input),
 * but it's URL-encoded for defense-in-depth before going into the `key` param.
 *
 * SFCC-11018 (ANALYTICS-30).
 */
export function resolveZendeskSnippetSrc({
  enabled,
  snippetId,
  isTpa,
}: ZendeskGate): string | null {
  if (isTpa || !enabled || !snippetId) return null;
  return `${ZENDESK_SNIPPET_BASE}${encodeURIComponent(snippetId)}`;
}
